import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Wrench,
  Package,
  Truck,
  Mail,
  Clock,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import SEO from '@/components/SEO';

const DEFAULT_ETA_MINUTES = 60;

const formatTime = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return { hours: pad(hours), minutes: pad(minutes), seconds: pad(seconds) };
};

const Maintenance = () => {
  const targetTime = useMemo(
    () => Date.now() + DEFAULT_ETA_MINUTES * 60 * 1000,
    []
  );
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const { hours, minutes, seconds } = formatTime(targetTime - now);
  const isOverdue = targetTime - now <= 0;

  const floatingBoxes = [
    { size: 'w-14 h-14', top: '10%', left: '8%', delay: 0, duration: 6 },
    { size: 'w-10 h-10', top: '22%', left: '85%', delay: 1.2, duration: 7 },
    { size: 'w-16 h-16', top: '70%', left: '12%', delay: 0.6, duration: 8 },
    { size: 'w-12 h-12', top: '78%', left: '80%', delay: 1.8, duration: 6.5 },
    { size: 'w-9 h-9', top: '45%', left: '92%', delay: 2.4, duration: 7.5 },
    { size: 'w-11 h-11', top: '55%', left: '4%', delay: 0.9, duration: 6.8 },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#1a0f05] via-[#2a1509] to-[#0f0703] text-white">
      <SEO
        title="Under Maintenance"
        description="Boxd Logistics is currently undergoing scheduled maintenance. We'll be back online shortly."
        robots="noindex, nofollow"
      />

      {/* Animated grid background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,140,50,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,140,50,0.5) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage:
            'radial-gradient(ellipse at center, black 40%, transparent 75%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 40%, transparent 75%)',
        }}
      />

      {/* Glowing orbs */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-96 w-96 rounded-full bg-blue-700/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-blue-800/20 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-emerald-500/10 blur-3xl" />

      {/* Floating package boxes */}
      {floatingBoxes.map((box, i) => (
        <motion.div
          key={i}
          className={`pointer-events-none absolute ${box.size} hidden md:block`}
          style={{ top: box.top, left: box.left }}
          initial={{ y: 0, rotate: 0, opacity: 0 }}
          animate={{
            y: [0, -24, 0],
            rotate: [0, 8, -6, 0],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: box.duration,
            delay: box.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <div className="relative h-full w-full rounded-xl border border-blue-500/30 bg-gradient-to-br from-blue-700/20 to-blue-900/10 shadow-[0_0_40px_rgba(59, 130, 246,0.15)] backdrop-blur-sm">
            <Package className="absolute inset-0 m-auto text-blue-300/70" />
          </div>
        </motion.div>
      ))}

      {/* Driving truck decoration */}
      <motion.div
        className="pointer-events-none absolute bottom-16 -left-24 hidden text-blue-500/30 md:block"
        initial={{ x: 0 }}
        animate={{ x: ['0vw', '120vw'] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
      >
        <Truck className="h-16 w-16" />
      </motion.div>

      <main className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-2xl"
        >
          {/* Glow ring */}
          <div className="pointer-events-none absolute -inset-px rounded-[2rem] bg-gradient-to-br from-blue-700/40 via-emerald-500/20 to-blue-800/40 opacity-60 blur" />

          <div className="relative rounded-[2rem] border border-white/10 bg-white/[0.04] p-8 shadow-2xl backdrop-blur-xl sm:p-12">
            {/* Status chip */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mx-auto mb-8 flex w-fit items-center gap-2 rounded-full border border-blue-500/30 bg-blue-700/10 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-blue-300"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
              </span>
              Scheduled Maintenance
            </motion.div>

            {/* Spinning gear */}
            <div className="relative mx-auto mb-8 flex h-28 w-28 items-center justify-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 via-blue-700 to-blue-900 p-[2px]"
              >
                <div className="h-full w-full rounded-full bg-[#1a0f05]" />
              </motion.div>
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                className="relative"
              >
                <Wrench className="h-12 w-12 text-blue-500 drop-shadow-[0_0_18px_rgba(59, 130, 246,0.6)]" />
              </motion.div>
              <Sparkles className="absolute -right-2 -top-2 h-5 w-5 text-emerald-300 animate-pulse" />
            </div>

            {/* Headline */}
            <h1 className="text-center text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
              We&apos;ll be right{' '}
              <span className="bg-gradient-to-r from-blue-300 via-blue-500 to-emerald-300 bg-clip-text text-transparent">
                back
              </span>
            </h1>
            <p className="mx-auto mt-4 max-w-lg text-center text-base text-white/70 sm:text-lg">
              Boxd Logistics is getting a quick tune-up. Our team is fine-tuning
              performance to get your shipments moving faster than ever.
            </p>

            {/* Countdown */}
            <div className="mt-10">
              <div className="mb-3 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50">
                <Clock className="h-3.5 w-3.5" />
                {isOverdue ? 'Wrapping up' : 'Estimated time remaining'}
              </div>
              <div className="flex items-center justify-center gap-3 sm:gap-4">
                {[
                  { label: 'Hours', value: hours },
                  { label: 'Minutes', value: minutes },
                  { label: 'Seconds', value: seconds },
                ].map((unit) => (
                  <div
                    key={unit.label}
                    className="flex w-20 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-4 sm:w-24"
                  >
                    <span className="bg-gradient-to-b from-white to-white/60 bg-clip-text font-mono text-3xl font-bold tabular-nums text-transparent sm:text-4xl">
                      {unit.value}
                    </span>
                    <span className="mt-1 text-[10px] uppercase tracking-widest text-white/40">
                      {unit.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-10">
              <div className="mb-2 flex items-center justify-between text-xs text-white/50">
                <span>Deploying improvements</span>
                <span className="text-blue-300">In progress</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                <motion.div
                  className="h-full w-1/3 rounded-full bg-gradient-to-r from-blue-500 via-emerald-300 to-blue-700"
                  animate={{ x: ['-100%', '300%'] }}
                  transition={{
                    duration: 2.4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="mailto:support@boxdlogistics.in"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-700 to-blue-800 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-800/30 transition hover:from-blue-500 hover:to-blue-700 hover:shadow-blue-700/40"
              >
                <Mail className="h-4 w-4" />
                Contact Support
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-medium text-white/80 transition hover:border-white/30 hover:bg-white/10 hover:text-white"
              >
                Retry now
              </button>
            </div>
          </div>

          {/* Footer note */}
          <p className="mt-6 text-center text-xs text-white/40">
            &copy; 2024 Boxd Logistics — Thanks for your patience.
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default Maintenance;
